import { Category, RequestStatus, SystemUserRole, SystemUserType, UserRoleBillingUserType, defaults } from 'typed/api';
import {
    combineReducers, entityAddAllReducer, entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer, entityLoadAllReducer,
    entityLoadManyReducer, entityLoadReducer, networkActionReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { deepAssign } from 'typed/utils';

const
    ucfas = defaults.NetworkArrayOf(defaults.UserCompanyFunctionalityAccess),
    pel = defaults.NetworkArrayOf(defaults.PublicExternalLink),
    sfl = defaults.NetworkObjectOf(''),
    uitls = defaults.NetworkArrayOf(defaults.UserInterfaceTranslationLanguage),
    qs = defaults.NetworkArrayOf(defaults.QuickStartReport),
    ua = defaults.NetworkArrayOf(defaults.UserActivityReport),
    uses = defaults.NetworkArrayOf(defaults.UserSyncExcelSettings);

export default combineReducers({
    initialLoadStatus: (state, action) => action.type === 'LOAD_APP_INITIAL_DATA' ? action.payload.status : state || RequestStatus.UNSYNCED,

    systemUserTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.SystemUserType), 'LOAD_ALL_SYSTEM_USER_TYPES', SystemUserType as any, 'SystemUserType'),
    systemUserRoles: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.SystemUserRole), 'LOAD_ALL_SYSTEM_USER_ROLES', SystemUserRole as any, 'SystemUserRole'),

    userCompanyFunctionalityAccesses: switchReducers(ucfas,
        entityLoadAllReducer(ucfas, 'LOAD_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES'),
        entityAddAllReducer(ucfas, 'ADD_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES'),
        entityEditAllReducer(ucfas, 'EDIT_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES'),
        entityDeleteAllReducer(ucfas, 'DELETE_ALL_USER_COMPANY_FUNCTIONALITY_ACCESSES')
    ),

    userRoleBillingUserTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.UserRoleBillingUserType), 'LOAD_ALL_USER_ROLE_BILLING_USER_TYPES', UserRoleBillingUserType as any, 'UserRoleBillingUserType'),
    categories: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.CategoryReport), 'LOAD_ALL_CATEGORIES', Category as any, 'CategoryReport'),

    userInterfaceTranslationLanguages: entityLoadAllReducer(uitls, 'LOAD_ALL_USER_INTERFACE_TRANSLATION_LANGUAGES'),
    quickStarts: entityLoadAllReducer(qs, 'LOAD_ALL_QUICK_STARTS'),

    publicExternalLinks: switchReducers(pel,
        entityLoadAllReducer(pel, 'LOAD_ALL_PUBLIC_EXTERNAL_LINKS'),
        entityLoadManyReducer(pel, 'LOAD_MANY_PUBLIC_EXTERNAL_LINKS'),
        entityLoadReducer(pel, 'LOAD_PUBLIC_EXTERNAL_LINK'),
        entityAddAllReducer(pel, 'ADD_ALL_PUBLIC_EXTERNAL_LINKS'),
        entityEditReducer(pel, 'EDIT_PUBLIC_EXTERNAL_LINK'),
        entityDeleteReducer(pel, 'DELETE_PUBLIC_EXTERNAL_LINK'),
        (state, action) => {
            const defaultState = state || pel;

            if (action.type === 'ADD_PUBLIC_EXTERNAL_LINK' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, {
                    Data: [...defaultState.Data, action.payload.response]
                });
            }

            if (action.type === 'LOAD_PUBLIC_EXTERNAL_LINK' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, {
                    Data: [
                        ...defaultState.Data.filter(({ IdentifierGuid }) => IdentifierGuid !== action.payload.identifierGuid),
                        action.payload.response
                    ]
                });
            }

            if (action.type === 'LOAD_PUBLIC_EXTERNAL_LINK' && action.payload.status === RequestStatus.FAILED) {
                return deepAssign(defaultState, {
                    Data: defaultState.Data.filter(({ IdentifierGuid }) => IdentifierGuid !== action.payload.identifierGuid)
                });
            }

            if (action.type === 'CLOSE_WORKSHOP' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, { Custom: { status: RequestStatus.UNSYNCED } });
            }

            return defaultState;
        }
    ),
    satisfactionFeedbackLink: switchReducers(sfl,
        networkActionReducer(sfl, 'LOAD_SATISFACTION_FEEDBACK_LINK'),
        (state, action) => {
            const defaultState = state || sfl;

            if (action.type === 'LOAD_SATISFACTION_FEEDBACK_LINK' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, { Data: action.payload.response });
            }

            return defaultState;
        }
    ),
    userActivityReports: switchReducers(ua,
        networkActionReducer(ua, 'LOAD_USER_ACTIVITY_REPORT'),
        (state, action) => {
            const defaultState = state || ua;

            if (action.type === 'LOAD_USER_ACTIVITY_REPORT' && action.payload.status === RequestStatus.SUCCEEDED) {
                const
                    newReport = action.payload.response,
                    data = defaultState.Data.filter(report => +report.PeriodStartDate !== +newReport.PeriodStartDate);

                return deepAssign(defaultState, {
                    Data: [...data, newReport]
                });
            }

            return defaultState;
        }
    ),
    userSyncExcelSettings: switchReducers(uses,
        entityLoadAllReducer(uses, 'LOAD_ALL_USER_SYNC_EXCEL_SETTINGS'),
        (state, action) => {
            const defaultState = state || uses;

            if (action.type === 'RERUN_USER_SYNC' && action.payload.status === RequestStatus.PENDING) {
                return deepAssign(defaultState, {
                    Data: {
                        [defaultState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: { syncPending: true }
                        }
                    }
                });
            }

            if (action.type === 'RERUN_USER_SYNC' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, {
                    Data: {
                        [defaultState.Data.getIndexByID(action.payload.entityID)]: action.payload.response
                    }
                });
            }

            return defaultState;
        }
    )
});
